import React from 'react'
import {
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Stack,
  Paper,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { styled, keyframes } from "@mui/material/styles";
import RestaurantIcon from "@mui/icons-material/Restaurant";

const MotionBox = motion(Box);

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const GradientText = styled("span")(({ theme }) => ({
  background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: "inherit",
  backgroundSize: "200% 200%",
  animation: `${shimmer} 3s ease-in-out infinite`,
}));

const FloatingPhone = styled(Box)(({ theme }) => ({
  animation: `${float} 4s ease-in-out infinite`,
}));

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const menuItems = [
    { 
      name: "Margherita Pizza", 
      price: "₹299", 
      popular: true,
      image: "https://images.unsplash.com/photo-1604068549290-dea0e4a305ca?w=100&h=100&fit=crop&auto=format",
      category: "Pizza"
    },
    { 
      name: "Paneer Butter Masala", 
      price: "₹349", 
      popular: true,
      image: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=100&h=100&fit=crop&auto=format",
      category: "Indian"
    },
    { 
      name: "Masala Dosa", 
      price: "₹199", 
      popular: true,
      image: "https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=100&h=100&fit=crop&auto=format",
      category: "Indian"
    },
    { 
      name: "Cold Coffee", 
      price: "₹129", 
      popular: false,
      image: "https://images.unsplash.com/photo-1517701604599-bb29b565090c?w=100&h=100&fit=crop&auto=format",
      category: "Beverages"
    },
    { 
      name: "Chocolate Shake", 
      price: "₹159", 
      popular: false,
      image: "https://images.unsplash.com/photo-1577805947697-89e18249d767?w=100&h=100&fit=crop&auto=format",
      category: "Beverages"
    },
  ];

  return (
    <Box
      sx={{
        minHeight: { xs: "auto", md: "100vh" },
        pt: { xs: 4, sm: 4, md: 4 },
        pb: { xs: 6, md: 6 },
        position: "relative",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* Background Image with Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url('https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=1800&auto=format&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: { xs: "scroll", md: "fixed" },
          zIndex: 0,
        }}
      />

      {/* Gradient Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: {
            xs: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,248,242,0.90) 100%)",
            md: "linear-gradient(to right, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.2) 100%)",
          },
          zIndex: 1,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 4, md: 8 },
            minHeight: { md: "80vh" },
            justifyContent: "space-between"
          }}
        >
          {/* Left Content - 45% width on desktop */}
          <Box 
            sx={{ 
              flex: { md: "0 0 45%" },
              maxWidth: { md: "45%" },
              width: "100%"
            }}
          >
            <MotionBox
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  bgcolor: "#fff",
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 0.8, md: 1 },
                  borderRadius: "30px",
                  mb: { xs: 3, md: 4 },
                  boxShadow: "var(--shadow)",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 8, md: 10 },
                    height: { xs: 8, md: 10 },
                    bgcolor: "var(--primary)",
                    borderRadius: "50%",
                    mr: 1,
                    animation: "pulse 2s infinite",
                  }}
                />
                <Typography sx={{ 
                  color: "var(--text)", 
                  fontSize: { xs: "11px", sm: "13px" }, 
                  fontFamily: "'DM Sans', sans-serif", 
                  lineHeight: 1.2,
                  letterSpacing: "1.1px",
                }}>
                  Smart Restaurant Management System
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: "2.2rem", sm: "3rem", md: "4rem" },
                  lineHeight: { xs: 1.1, md: 1.2 },
                  letterSpacing: { xs: "1px", md: "3px" },
                  fontWeight: 900,
                  color: "var(--text)",
                  mb: { xs: 2, md: 3 },
                  fontFamily: "'Archivo Black', sans-serif",
                }}
              >
                QR-Based Ordering
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    color: "var(--primary)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <GradientText>Smart Management</GradientText>
                </Box>
                Happy Customers
              </Typography>

              <Typography
                sx={{
                  color: "var(--text-light)",
                  fontSize: { xs: "15px", sm: "16px", md: "18px" },
                  lineHeight: { xs: 1.6, md: 1.8 },
                  maxWidth: { xs: "100%", md: "550px" },
                  mb: { xs: 3, md: 5 },
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: "1.1px",
                }}
              >
                Complete QR-based ordering and restaurant management system. 
                Register your restaurant, manage menus, track orders, and grow your business.
              </Typography>

              <Stack 
                direction={{ xs: "column", sm: "row" }} 
                spacing={2} 
                flexWrap="wrap"
                sx={{ width: "100%" }}
              >
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  fullWidth={isSmallMobile}
                  sx={{
                    bgcolor: "var(--primary)",
                    color: "#fff",
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    boxShadow: "var(--shadow-orange)",
                    "&:hover": {
                      bgcolor: "var(--primary-dark)",
                      transform: "translateY(-3px)",
                      boxShadow: "var(--shadow-orange-hover)",
                    },
                  }}
                >
                  Register Restaurant
                </Button>

                <Button
                  component={Link}
                  to="/restaurants"
                  variant="outlined"
                  fullWidth={isSmallMobile}
                  sx={{
                    color: "var(--text)",
                    borderColor: "var(--text)",
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.2, md: 1.5 },
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    "&:hover": {
                      borderColor: "var(--primary)",
                      color: "var(--primary)",
                      bgcolor: "var(--bg-primary-soft)",
                    },
                  }}
                >
                  Explore Restaurants
                </Button>
              </Stack>

              {/* <Box 
                sx={{ 
                  display: "flex", 
                  gap: { xs: 2, sm: 4 }, 
                  mt: { xs: 3, md: 5 },
                  flexWrap: "wrap",
                  justifyContent: { xs: "center", md: "flex-start" }
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.5rem", md: "2rem" }, color: "var(--text)" }}>
                    500+
                  </Typography>
                  <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "12px", md: "14px" } }}>Restaurants</Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.5rem", md: "2rem" }, color: "var(--text)" }}>
                    10K+
                  </Typography>
                  <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "12px", md: "14px" } }}>Daily Orders</Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: "1.5rem", md: "2rem" }, color: "var(--text)" }}>
                    4.8⭐
                  </Typography>
                  <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "12px", md: "14px" } }}>Average Rating</Typography>
                </Box>
              </Box> */}
            </MotionBox>
          </Box>

          {/* Right Content - Phone Mockup with Menu */}
          <Box 
            sx={{ 
              flex: { md: "0 0 50%" },
              maxWidth: { md: "50%" },
              width: "100%",
              display: {  md: "flex" },
              justifyContent: "center",
              alignItems: "center",
              mt:5
            }}
          >
            <MotionBox
              initial={{ opacity: 0, x: 80, rotate: 5 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              sx={{ 
                display: "flex", 
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              {/* Floating Animation */}
              <FloatingPhone>
                {/* Phone Mockup */}
                <Box
                  sx={{
                    width: {xs:340,sm:340, md: 340, lg: 380 },
                    height: {xs:740,sm:740, md: 680, lg: 760 },
                    borderRadius: "45px",
                    bgcolor: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                    p: 3,
                    boxShadow: "0 40px 100px rgba(0,0,0,0.35), 0 0 0 2px rgba(255,255,255,0.05)",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 14,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 150,
                      height: 24,
                      bgcolor: "#1a1a2e",
                      borderRadius: "20px",
                      zIndex: 10,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 18,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 60,
                      height: 5,
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderRadius: "10px",
                      zIndex: 11,
                    },
                  }}
                >
                  {/* Phone Screen */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "30px",
                      bgcolor: "#ffffff",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      mt: 2,
                    }}
                  >
                    {/* Phone Header - Hero Image */}
                    <Box
                      sx={{
                        position: "relative",
                        height: 120,
                        background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                        overflow: "hidden",
                      }}
                    >
                      {/* Hero Image inside Phone */}
                      <Box
                        component="img"
                        src="https://images.unsplash.com/photo-1552566626-52f8b828add9?w=600&h=200&fit=crop&auto=format"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: 0.6,
                        }}
                      />
                      {/* Overlay Content */}
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "#fff",
                          p: 2,
                        }}
                      >
                        <RestaurantIcon sx={{ fontSize: 28, mb: 0.5 }} />
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                          }}
                        >
                          Restaurantwala
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            opacity: 0.9,
                            fontWeight: 500,
                          }}
                        >
                          ★ 4.8 • 500+ Orders Today
                        </Typography>
                      </Box>
                    </Box>

                    {/* Menu Items */}
                    <Box sx={{ p: 2, flex: 1, overflow: "auto", bgcolor: "#f8faf8" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            color: "var(--text)",
                          }}
                        >
                          📋 Today's Special
                        </Typography>
                        <Chip
                          label="12 Items"
                          size="small"
                          sx={{
                            bgcolor: "var(--bg-primary-soft)",
                            color: "var(--primary)",
                            fontWeight: 600,
                            fontSize: "9px",
                            height: 20,
                          }}
                        />
                      </Box>

                      {/* Category Chips */}
                      <Box sx={{ display: "flex", gap: 0.8, mb: 2, overflow: "auto", pb: 0.5 }}>
                        {["All", "Pizza", "Indian", "Snacks", "Beverages"].map((cat, i) => (
                          <Chip
                            key={i}
                            label={cat}
                            size="small"
                            sx={{
                              bgcolor: i === 0 ? "var(--primary)" : "#fff",
                              color: i === 0 ? "#fff" : "var(--text)",
                              fontWeight: 600,
                              fontSize: "9px",
                              height: 24,
                              border: i !== 0 ? "1px solid var(--border)" : "none",
                              "&:hover": {
                                bgcolor: i === 0 ? "var(--primary-dark)" : "var(--bg-primary-soft)",
                              },
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                          />
                        ))}
                      </Box>

                      {/* Menu Items List */}
                      <Stack spacing={1.2}>
                        {menuItems?.map((item, index) => (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 1.2,
                              borderRadius: "14px",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              bgcolor: "#ffffff",
                              border: "1px solid var(--border)",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                borderColor: "var(--primary)",
                                transform: "translateX(6px) scale(1.02)",
                                boxShadow: "var(--shadow-orange)",
                              },
                              cursor: "pointer",
                            }}
                          >
                            {/* Item Image */}
                            <Avatar
                              src={item.image}
                              alt={item.name}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "12px",
                                flexShrink: 0,
                                border: "2px solid var(--border)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                  borderColor: "var(--primary)",
                                },
                              }}
                            />
                            
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    color: "var(--text)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                                {item.popular && (
                                  <Chip
                                    label="🔥"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "8px",
                                      bgcolor: "rgba(255,107,0,0.1)",
                                      color: "var(--primary)",
                                      fontWeight: 700,
                                      p: 0,
                                      "& .MuiChip-label": { px: 0.5 },
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                sx={{
                                  color: "var(--text-light)",
                                  fontSize: "0.6rem",
                                }}
                              >
                                ⭐ 4.5 • {item.popular ? "Bestseller" : "Available"}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.85rem",
                                  color: "var(--primary)",
                                }}
                              >
                                {item.price}
                              </Typography>
                              <Button
                                size="small"
                                sx={{
                                  minWidth: 26,
                                  height: 26,
                                  borderRadius: "8px",
                                  bgcolor: "var(--primary)",
                                  color: "#fff",
                                  fontSize: "13px",
                                  p: 0,
                                  fontWeight: 700,
                                  "&:hover": {
                                    bgcolor: "var(--primary-dark)",
                                    transform: "scale(1.15) rotate(90deg)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                +
                              </Button>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>

                      {/* Bottom Order Summary */}
                      <Box
                        sx={{
                          mt: 2,
                          pt: 1.5,
                          borderTop: "2px solid var(--border)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          bgcolor: "#fff",
                          borderRadius: "14px",
                          p: 1.5,
                          boxShadow: "0 -4px 20px rgba(0,0,0,0.04)",
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: "0.65rem", color: "var(--text-light)" }}>
                            Cart • 3 items
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "var(--text)" }}>
                            ₹847
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            bgcolor: "var(--primary)",
                            color: "#fff",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            px: 3,
                            py: 1,
                            boxShadow: "var(--shadow-orange)",
                            "&:hover": {
                              bgcolor: "var(--primary-dark)",
                              transform: "translateY(-2px)",
                              boxShadow: "var(--shadow-orange-hover)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Place Order
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </FloatingPhone>

              {/* Floating Decorative Badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: -20,
                  right: -30,
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  p: 1.5,
                  boxShadow: "var(--shadow-orange)",
                  border: "2px solid var(--primary)",
                  display: { xs: "none", lg: "flex" },
                  alignItems: "center",
                  gap: 1,
                  animation: `${float} 3s ease-in-out infinite`,
                  animationDelay: "1s",
                }}
              >
                <Box sx={{ fontSize: "1.5rem" }}>🔥</Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: "var(--text)" }}>
                    30% OFF
                  </Typography>
                  <Typography sx={{ fontSize: "0.6rem", color: "var(--text-light)" }}>
                    First Order
                  </Typography>
                </Box>
              </Box>

              {/* Floating Rating Badge */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: -70,
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  p: 1.5,
                  boxShadow: "var(--shadow)",
                  border: "1px solid var(--border)",
                  display: { xs: "none", lg: "flex" },
                  alignItems: "center",
                  gap: 1,
                  animation: `${float} 4s ease-in-out infinite`,
                  animationDelay: "0.5s",
                }}
              >
                <Box sx={{ fontSize: "1.5rem" }}>⭐</Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: "var(--text)" }}>
                    4.8/5
                  </Typography>
                  <Typography sx={{ fontSize: "0.6rem", color: "var(--text-light)" }}>
                    500+ Reviews
                  </Typography>
                </Box>
              </Box>
            </MotionBox>
          </Box>
        </Box>
      </Container>

      {/* Add pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.5); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </Box>
  )
}

export default Hero